// @platform/ui canonical design tokens. Loaded ONCE at the app shell so
// every plugin chunk can reference `var(--brand-primary)`, `var(--warning)`,
// `var(--bg-page)`, etc. without each having to import tokens.css itself.
//
// Why this lives in the app shell and not in @platform/ui's package entry:
// Backstage's plugin build pipeline (`backstage-cli package build`, Rollup)
// silently strips side-effect CSS imports from plugin chunks — even with
// the consuming package declaring `"sideEffects": ["*.css"]` and the
// plugin's own code adding an explicit `import '@platform/ui/src/tokens.css'`.
// Surfaced 2026-06-03 during PR #210 Phase 4 verify: the drift banner
// rendered as plain text in prod because no plugin chunk had the CSS
// variables it depends on. Putting the import here, in the app target
// (webpack), makes the CSS a guaranteed part of the global stylesheet
// every plugin inherits — fixes the issue for every current and future
// @platform/ui consumer in one place.
import '@platform/ui/src/tokens.css';
// Points the Backstage UI (--bui-*) theme tokens at the tokens above, so
// upstream components built on @backstage/ui render in the design system.
// Loaded here, once, for the same reason as tokens.css. The file is unlayered,
// so it outranks BUI's own `@layer tokens` values whatever the load order.
import '@platform/ui/src/bui-bridge.css';
// Shared @platform/ui component classes (.tote-btn and its variants),
// translated from v2-prototype.html. Loaded here, once, for the same reason
// as tokens.css; @platform/ui's Button renders these class names.
import '@platform/ui/src/components.css';
import './catalogHome.css';

import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { GatedCatalogImportPage } from './components/catalogImport/GatedCatalogImportPage';
import { eagerLegacyPages } from './eagerLegacyPages';

import { AlertDisplay, SignInPage } from '@backstage/core-components';
import { createApp, CreateAppOptions } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import {
  AppRootElementBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { UnifiedThemeProvider } from '@backstage/theme';
import { toteCompactLight, toteCompactDark } from './theme/toteCompactTheme';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  AppComponents,
  AppTheme,
  githubAuthApiRef,
} from '@backstage/core-plugin-api';
// import { RbacPage } from '@backstage-community/plugin-rbac';
import { RbacPage } from '@internal/backstage-plugin-custom-rbac';
import { DeployV3InboxPage } from '@internal/backstage-plugin-deploy-v3';
import {
  CdImageUpdateWorkflowPage,
  PickShaPage,
  UpdateServicePage,
  PreviewPage,
  ServiceShaPage,
} from '@internal/backstage-plugin-cd-image-update-workflow';
// import { GithubWorkflowsPage } from '@internal/backstage-plugin-github-workflows';
// import { CiCdPage, LogPage } from '@internal/backstage-plugin-ci-cd';
import {
  PickReplicaPage,
  PreviewReplicaPage,
  ScalingWorkflowPage,
  ServiceReplicaPage,
  UpdateServiceReplicaPage,
} from '@internal/backstage-plugin-scaling';
import {
  EnvironmentVariablesPage,
  PreviewManifestPage,
  ServiceCreatorPage,
  ManifestOptionsPage,
  GatewayManifestPage,
  GatewayPreviewPage,
  HttpRoutePage,
  HttpRoutePreviewPage,
  CronJobGeneratorPage,
  CronJobPreviewPage,
  CronJobEnvironmentVariablesPage,
} from '@internal/backstage-plugin-service-creator';
import {
  CloudSqlPage,
  InfraCreatorPage,
  RedisPage,
  ResourceCreatorPage,
} from '@internal/backstage-plugin-infra-creator';
import { GcrInfoPage } from '@internal/backstage-plugin-gcr-info';
import {
  AddExternalSecretPage,
  ExternalSecretPage,
  GkeOptionsPage,
  InstallExternalSecretPage,
  SetUpExternalSecretPage,
} from '@internal/backstage-plugin-gke-options';
import { EnvPage } from '@internal/backstage-plugin-env';

const themes: AppTheme[] = [
  {
    id: 'tote-compact-light',
    title: 'Tote Light',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={toteCompactLight} children={children} />
    ),
  },
  {
    id: 'tote-compact-dark',
    title: 'Tote Dark',
    variant: 'dark',
    icon: <DarkIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={toteCompactDark} children={children} />
    ),
  },
];

const components: Partial<AppComponents> = {
  SignInPage: props => (
    <SignInPage
      {...props}
      auto
      provider={{
        id: 'github-auth-provider',
        title: 'GitHub',
        message: 'Sign in using GitHub',
        apiRef: githubAuthApiRef,
      }}
    />
  ),
};

// Unchanged from the legacy createApp options. The new route binder accepts
// the legacy route refs: core-compat-api converts them in place, so a binding
// still matches the ref object that the plugin's page mounts.
const bindRoutes: CreateAppOptions['bindRoutes'] = ({ bind }) => {
  bind(catalogPlugin.externalRoutes, {
    createComponent: scaffolderPlugin.routes.root,
    viewTechDoc: techdocsPlugin.routes.docRoot,
    createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
  });
  bind(apiDocsPlugin.externalRoutes, {
    registerApi: catalogImportPlugin.routes.importPage,
  });
  bind(scaffolderPlugin.externalRoutes, {
    registerComponent: catalogImportPlugin.routes.importPage,
    viewTechDoc: techdocsPlugin.routes.docRoot,
  });
  bind(orgPlugin.externalRoutes, {
    catalogIndex: catalogPlugin.routes.catalogIndex,
  });
};

// Backward-compat shim for deploy-v3's top-level URLs.
// The backend headerState synthesizer generates URLs like
//   /deploy-v3/<encoded-entityRef>?...
// The v3 plugin only ships an entity-tab surface today (no top-level
// page yet — that's the future approval-inbox), so these URLs would
// 404 without this shim. Redirects to the entity tab while preserving
// query string. Parses entityRefs of the form "kind:namespace/name".
const DeployV3RedirectShim = () => {
  const params = useParams();
  const location = useLocation();
  // FlatRoutes mounts this route as /deploy-v3/:entityRef/*, so params['*'] is
  // '' rather than undefined; read the encoded ref from params.entityRef first.
  const raw = params.entityRef || params['*'] || '';
  const entityRef = decodeURIComponent(raw);
  const match = /^([^:]+):([^/]+)\/(.+)$/.exec(entityRef);
  if (!match) {
    return <Navigate to="/catalog" replace />;
  }
  const [, kind, namespace, name] = match;
  const target = `/catalog/${namespace}/${kind}/${name}/deploy-v3${location.search}`;
  return <Navigate to={target} replace />;
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/deploy-v3" element={<DeployV3InboxPage />} />
    <Route path="/deploy-v3/:entityRef" element={<DeployV3RedirectShim />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/catalog-import" element={<GatedCatalogImportPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route
      path="/cd-image-update-workflow"
      element={<CdImageUpdateWorkflowPage />}
    />
    <Route
      path="/cd-image-update-workflow/pick-sha"
      element={<PickShaPage />}
    />
    <Route
      path="/cd-image-update-workflow/update-service"
      element={<UpdateServicePage />}
    />
    <Route
      path="/cd-image-update-workflow/previewPage"
      element={<PreviewPage />}
    />
    <Route
      path="/cd-image-update-workflow/serviceShaPage"
      element={<ServiceShaPage />}
    />
    {/* <Route path="/github-workflows" element={<GithubWorkflowsPage />} />
    <Route path="/ci-cd" element={<CiCdPage />} />
    <Route path="/ci-cd/log" element={<LogPage />} /> */}
    <Route path="/scaling" element={<ScalingWorkflowPage />} />
    <Route path="/scaling/pick-replica" element={<PickReplicaPage />} />
    <Route
      path="/scaling/update-service-replica"
      element={<UpdateServiceReplicaPage />}
    />
    <Route
      path="/scaling/previewPageReplica"
      element={<PreviewReplicaPage />}
    />
    <Route
      path="/scaling/serviceReplicaPage"
      element={<ServiceReplicaPage />}
    />
    <Route path="/service-creator" element={<ManifestOptionsPage />} />
    <Route
      path="/service-creator/manifest-generator"
      element={<ServiceCreatorPage />}
    />
    <Route
      path="/service-creator/environment"
      element={<EnvironmentVariablesPage />}
    />
    <Route path="/service-creator/preview" element={<PreviewManifestPage />} />
    <Route
      path="/service-creator/gateway-manifest"
      element={<GatewayManifestPage />}
    />
    <Route
      path="/service-creator/gateway-manifest/preview"
      element={<GatewayPreviewPage />}
    />
    <Route
      path="/service-creator/http-route-manifest"
      element={<HttpRoutePage />}
    />
    <Route
      path="/service-creator/preview-http-route-manifest"
      element={<HttpRoutePreviewPage />}
    />
    <Route
      path="/service-creator/cron-job-generator"
      element={<CronJobGeneratorPage />}
    />
    <Route
      path="/service-creator/cron-job-environment-variables"
      element={<CronJobEnvironmentVariablesPage />}
    />
    <Route
      path="/service-creator/cron-job-manifest-preview"
      element={<CronJobPreviewPage />}
    />
    <Route path="/infra-creator" element={<InfraCreatorPage />} />
    <Route path="/infra-creator/resource" element={<ResourceCreatorPage />} />
    <Route path="/infra-creator/cloudsql" element={<CloudSqlPage />} />
    <Route path="/infra-creator/redis" element={<RedisPage />} />
    <Route path="/gcr-info" element={<GcrInfoPage />} />
    <Route path="/gke-options" element={<GkeOptionsPage />} />
    <Route
      path="/gke-options/external-secret"
      element={<ExternalSecretPage />}
    />
    <Route
      path="/gke-options/external-secret/add"
      element={<AddExternalSecretPage />}
    />
    <Route
      path="/gke-options/external-secret/install"
      element={<InstallExternalSecretPage />}
    />
    <Route
      path="/gke-options/external-secret/setup"
      element={<SetUpExternalSecretPage />}
    />
    <Route path="/env" element={<EnvPage />} />
  </FlatRoutes>
);

// The app's alert display stays the core-components AlertDisplay (a Material UI
// snackbar). @backstage/plugin-app installs its own 'alert-display' root
// element, a Backstage UI toast; this module replaces it by extension id
// (app-root-element:app/alert-display). The OAuth request dialog needs no
// override: plugin-app renders the same core-components OAuthRequestDialog.
const alertDisplayModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootElementBlueprint.make({
      name: 'alert-display',
      params: { element: <AlertDisplay /> },
    }),
  ],
});

// The app runs on the New Frontend System in hybrid mode. convertLegacyAppRoot
// is given the whole AppRouter tree, not the FlatRoutes element alone: from
// that tree it takes Root as the app layout (the Tote sidebar, with
// NotificationsSystem inside it) and disables the default navigation. Given
// FlatRoutes alone, it would return only the routes and the default layout
// would replace Root. The routes stay in this file, where
// src/__tests__/routeInventory.test.ts reads them.
//
// eagerLegacyPages renders each converted page from the same routes without
// the lazy loader that the conversion gives it, which otherwise delays the
// first render and empties the content area on every page change
// (src/eagerLegacyPages.tsx).
//
// createApp is called once, here, at module scope: it prepares the app
// lazily on first render and reuses it for every later createRoot() call.
const app = createApp({
  features: [
    convertLegacyAppOptions({ apis, themes, components }),
    ...convertLegacyAppRoot(
      <AppRouter>
        <Root>{routes}</Root>
      </AppRouter>,
    ),
    alertDisplayModule,
  ],
  bindRoutes,
  advanced: { extensionFactoryMiddleware: eagerLegacyPages(routes) },
});

// The default export stays a component, so index.tsx renders <App /> as before.
const App = () => app.createRoot();

export default App;
